import { useState } from "react";
import { Send, Loader2, Copy as CopyIcon, Check, X } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

/* Spec §7.3 — Send a Copy: one-tap redacted snapshot ready for the OS share sheet.
   Never includes PNR/ticket/seat/cost/loyalty/passport/health/contacts/notes. */
export default function SendCopyButton({ tripId, tripTitle }) {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null); // string when dialog is open
  const [copied, setCopied] = useState(false);

  const build = async (openDialog = false) => {
    setBusy(true);
    try {
      const r = await api.post(`/trips/${tripId}/copy`, { format: "text" });
      const text = r?.data?.snapshot || "";
      // Try native share first (best on iOS/Android). Otherwise open the preview dialog.
      if (!openDialog && navigator.share) {
        try {
          await navigator.share({
            title: tripTitle || "My trip plan",
            text,
          });
          toast.success("Shared");
          return;
        } catch (e) {
          if (e?.name === "AbortError") return; // user cancelled
          // fall through to dialog
        }
      }
      setPreview(text);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not build a copy — try again shortly");
    } finally {
      setBusy(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(preview);
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error("Copy not available in this browser");
    }
  };

  return (
    <>
      <button
        onClick={() => build(false)}
        disabled={busy}
        data-testid="trip-send-copy-btn"
        title="One-tap redacted share — safe for family & friends"
        className="mt-2 ml-2 inline-flex items-center gap-1.5 border border-fuchsia-400 bg-fuchsia-50 text-fuchsia-700 px-3 py-2 text-sm font-700 hover:bg-fuchsia-100 transition-colors disabled:opacity-50">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        Send a Copy
      </button>

      {preview !== null && (
        <div
          onClick={() => setPreview(null)}
          className="fixed inset-0 bg-black/50 z-[80] flex items-end sm:items-center justify-center p-3"
          data-testid="send-copy-dialog">
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <div>
                <div className="text-[10px] font-800 uppercase tracking-widest text-fuchsia-600">Send a Copy · Redacted</div>
                <div className="font-serif font-800 text-lg text-slate-900">{tripTitle}</div>
              </div>
              <button onClick={() => setPreview(null)} data-testid="send-copy-close"
                className="text-slate-400 hover:text-slate-700 p-1"><X className="w-5 h-5" /></button>
            </div>
            <pre
              data-testid="send-copy-snapshot"
              className="max-h-[45vh] overflow-y-auto bg-slate-50 text-[13px] text-slate-800 p-4 whitespace-pre-wrap font-mono">
{preview}
            </pre>
            <div className="px-5 py-3 bg-white border-t border-slate-200 flex items-center justify-end gap-2">
              <button
                onClick={copyToClipboard}
                data-testid="send-copy-clipboard"
                className="inline-flex items-center gap-1.5 border border-slate-300 text-slate-700 px-3 py-2 text-sm font-700 hover:bg-slate-50">
                {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <CopyIcon className="w-4 h-4" />}
                {copied ? "Copied" : "Copy"}
              </button>
              {navigator.share && (
                <button
                  onClick={async () => {
                    try { await navigator.share({ title: tripTitle, text: preview }); toast.success("Shared"); setPreview(null); }
                    catch (e) { if (e?.name !== "AbortError") toast.error("Share not available"); }
                  }}
                  data-testid="send-copy-native-share"
                  className="inline-flex items-center gap-1.5 bg-fuchsia-600 text-white px-3 py-2 text-sm font-800 hover:bg-fuchsia-700">
                  <Send className="w-4 h-4" /> Share…
                </button>
              )}
            </div>
            <div className="px-5 pb-4 text-[11px] text-slate-500">
              This snapshot never contains confirmation numbers, seats, prices, loyalty numbers, passports, health records, contacts or home addresses.
            </div>
          </div>
        </div>
      )}
    </>
  );
}
